import React from 'react';
import { format } from 'date-fns';

import Link from 'next/link';
import IMAGE_SIZE, { type ImageSize } from '@/constants/image-size.constants';
import { getImageUrl } from '@/services/media.service';
import { fractionFormatter } from '@/utils/products';
import Picture from '../Picture';
import { stringHasValue } from '@/utils/data';
import type { TypeOnDemandWithoutUnresolvableLinksResponse } from '@/contentful/types';

type OnDemandSummaryCardProps = {
  content: TypeOnDemandWithoutUnresolvableLinksResponse;
  imageSize: ImageSize;
  className?: string;
  showPrice?: boolean;
};

const OnDemandSummaryCard = ({
  content,
  imageSize,
  className,
  showPrice
}: OnDemandSummaryCardProps) => {
  let url;

  const {
    title,
    date,
    featuredImage,
    summary,
    price,
    sectors,
    locations,
    categories,
    primaryTag
  } = content.fields;

  const tags = [];
  if (sectors) tags.push(...sectors);
  if (locations) tags.push(...locations);
  if (categories) tags.push(...categories);

  if (featuredImage?.fields.file?.url.includes('images.ctfassets.net')) {
    url = getImageUrl(featuredImage.fields.file.url);
    featuredImage.fields.file.url = url?.href ?? '';
  }

  return (
    <Link
      prefetch={false}
      href={`/on-demand/${content.sys.id}`}
      className={`ondemand-card ${className}`}
    >
      <div className='ondemand-card__media'>
        {featuredImage && url && stringHasValue(url.href) ? (
          <div className='ondemand-card__media-main'>
            <Picture
              src={url.href}
              width={imageSize ?? IMAGE_SIZE.RELATED__SMALL}
            />
          </div>
        ) : (
          <div
            className='ondemand-card__media-main'
            style={{
              backgroundImage: 'url("/static/imgs/trending/trending.jpg")'
            }}
          >
            {}
          </div>
        )}
      </div>
      <div className='ondemand-card__header'>
        <div
          className='category-article-card__category'
          style={{ display: 'flex' }}
        >
          {primaryTag && (
            <div className='standard-article-card__category-label'>
              {primaryTag?.fields.name}
            </div>
          )}
          <div className='ondemand-card__date'>
            {format(new Date(date), 'dd MMM yy')}
          </div>
        </div>
        <h2 className='ondemand-card__title'>{title}</h2>
        <div className='ondemand-card__price'>
          {showPrice && (
            <span>
              {price && price > 0 ? fractionFormatter.format(price) : 'Free'}
            </span>
          )}
        </div>
      </div>
      <div className='overview'>{summary}</div>
      {tags.length > 0 && (
        <div className='categories'>
          {tags.map((tag, index) => (
            <div key={index} className='category-pill'>
              {tag?.fields.name}
            </div>
          ))}
        </div>
      )}
    </Link>
  );
};

export default OnDemandSummaryCard;
